import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Link2 } from 'lucide-react'
import { api, setApiJwt } from '../../api/client'
import { useSession } from '../../store/user.store'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

const OTHER_PLATFORM_LABEL: Record<'telegram' | 'max', string> = {
  telegram: 'Telegram',
  max: 'MAX',
}

/**
 * Compact entry point for cross-platform account linking on /profile.
 * - Already linked → single read-only "connected" pill row.
 * - Not linked → single button that opens LinkingModal with the full
 *   redeem + generate-code UX. Most users never touch this, so keeping
 *   it folded saves vertical space.
 */
export function LinkingCard() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const currentPlatform = useSession((s) => s.user?.platform)
  const otherPlatform: 'telegram' | 'max' | null =
    currentPlatform === 'telegram' ? 'max' : currentPlatform === 'max' ? 'telegram' : null

  const statusQ = useQuery({
    queryKey: ['me', 'link'],
    queryFn: () => api.getLinkStatus(),
    staleTime: 30_000,
  })
  const linkedPlatforms = statusQ.data?.platforms ?? []
  const alreadyLinked = otherPlatform ? linkedPlatforms.includes(otherPlatform) : false

  if (!otherPlatform) return null

  if (alreadyLinked) {
    return (
      <div className="flex items-center gap-3 rounded-2xl bg-white/70 p-3 ring-1 ring-sage-200/60">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
          <Link2 className="h-4 w-4" strokeWidth={1.8} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-sage-900">{t('linking.connected_title')}</p>
          <p className="text-xs text-sage-600">
            {t('linking.connected_subtitle', { platform: OTHER_PLATFORM_LABEL[otherPlatform] })}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-2xl bg-white/70 p-3 text-left ring-1 ring-sage-200/60 transition hover:bg-white active:scale-[0.99]"
      >
        <img
          src="/icons/profile-linking.png"
          alt=""
          aria-hidden="true"
          className="h-10 w-10 shrink-0 select-none"
          draggable={false}
        />
        <div className="min-w-0 flex-1">
          <p className="font-serif text-sm text-sage-800 leading-snug">{t('linking.title')}</p>
          <p className="mt-0.5 text-xs text-sage-500">
            {t('linking.compact_subtitle', { platform: OTHER_PLATFORM_LABEL[otherPlatform] })}
          </p>
        </div>
        <Link2 className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.8} aria-hidden />
      </button>

      <LinkingModal open={open} onClose={() => setOpen(false)} otherPlatform={otherPlatform} />
    </>
  )
}

function LinkingModal({
  open,
  onClose,
  otherPlatform,
}: {
  open: boolean
  onClose: () => void
  otherPlatform: 'telegram' | 'max'
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const startMut = useMutation({
    mutationFn: () => api.startLink(),
    onError: (err) => toast.error((err as Error).message),
  })

  const [redeemInput, setRedeemInput] = useState('')
  const redeemMut = useMutation({
    mutationFn: (code: string) => api.redeemLink(code),
    onSuccess: (data) => {
      if (data.jwt) {
        setApiJwt(data.jwt)
      }
      toast.success(t('linking.merged_success'))
      void qc.invalidateQueries()
      setTimeout(() => window.location.reload(), 800)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const startData = startMut.data
  const myDeeplink = otherPlatform === 'telegram' ? startData?.deeplinkTelegram : startData?.deeplinkMax

  return (
    <Modal open={open} onClose={onClose} title={t('linking.title')}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-sage-700">
          {t('linking.subtitle', { platform: OTHER_PLATFORM_LABEL[otherPlatform] })}
        </p>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-sage-600">
            {t('linking.have_code_label', { platform: OTHER_PLATFORM_LABEL[otherPlatform] })}
          </label>
          <div className="flex gap-2">
            <input
              value={redeemInput}
              onChange={(e) => setRedeemInput(e.target.value.toUpperCase())}
              placeholder="XXXXXX"
              maxLength={10}
              className="flex-1 rounded-xl border border-sage-200 bg-white px-3 py-2 font-mono text-sm uppercase tracking-widest outline-none focus:border-sage-500"
            />
            <Button
              variant="secondary"
              onClick={() => redeemMut.mutate(redeemInput.trim())}
              disabled={redeemInput.trim().length < 4 || redeemMut.isPending}
              loading={redeemMut.isPending}
            >
              {t('linking.redeem_button')}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-sage-500">
          <div className="h-px flex-1 bg-sage-200" />
          <span>{t('linking.or')}</span>
          <div className="h-px flex-1 bg-sage-200" />
        </div>

        <Button
          variant={startData ? 'ghost' : 'primary'}
          className="w-full"
          onClick={() => startMut.mutate()}
          loading={startMut.isPending}
        >
          {startData ? t('linking.new_code') : t('linking.generate_code')}
        </Button>

        {startData && (
          <div className="rounded-xl bg-sage-50 p-3">
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest text-sage-600">
                {t('linking.code_label')}
              </div>
              <div className="mt-1 font-mono text-2xl font-semibold tracking-[0.3em] text-sage-900">
                {startData.code}
              </div>
              <div className="mt-1 text-xs text-sage-600">
                {t('linking.code_hint', { platform: OTHER_PLATFORM_LABEL[otherPlatform] })}
              </div>
            </div>
            {myDeeplink && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2 ring-1 ring-sage-200">
                <span className="flex-1 truncate font-mono text-xs text-sage-700">{myDeeplink}</span>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(myDeeplink)
                    toast.success(t('linking.link_copied'))
                  }}
                  className="rounded px-2 py-0.5 text-xs text-sage-800 hover:bg-sage-100"
                >
                  {t('linking.copy')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
